import React, { createContext, useState, useEffect, useMemo } from 'react'

/**
 * CaseDetailContext — port of Scale's CaseDetailContext provider.
 * Provides shared case state to all case-detail subcomponents so they
 * don't have to re-fetch or pass props through every level.
 */
export const CaseDetailContext = createContext(null)

export function CaseDetailProvider({ children, initialCaseData = null, refreshCaseData = null }) {
    const [caseData, setCaseData] = useState(initialCaseData)
    const [transferHistory, setTransferHistory] = useState(null)

    // Sync internal state with the latest prop reference — fires when
    // refreshCaseData() finishes a refetch and the parent passes a new
    // object (e.g., right after creating an invoice).
    useEffect(() => {
        if (initialCaseData) setCaseData(initialCaseData)
    }, [initialCaseData])

    // partyLookup: maps every client_id (primary + additional parties) to the
    // primary plaintiff/defendant id and role. Used by FinanceTable +
    // EmailReminder + ProgressTracker.
    const partyLookup = useMemo(() => {
        const lookup = {}
        if (caseData?.plaintiff?.clientId) {
            lookup[caseData.plaintiff.clientId] = {
                primary_party_id: caseData.plaintiff.clientId,
                client_id: caseData.plaintiff.clientId,
                role: 'plaintiff',
            }
        }
        ;(Array.isArray(caseData?.plaintiff?.additionalParties) ? caseData.plaintiff.additionalParties : []).forEach((p) => {
            const cid = p.client?.client_id || p.client_id
            if (cid) lookup[cid] = { primary_party_id: caseData.plaintiff.clientId, client_id: cid, role: p.role || 'plaintiff' }
        })
        if (caseData?.defendant?.clientId) {
            lookup[caseData.defendant.clientId] = {
                primary_party_id: caseData.defendant.clientId,
                client_id: caseData.defendant.clientId,
                role: 'defendant',
            }
        }
        ;(Array.isArray(caseData?.defendant?.additionalParties) ? caseData.defendant.additionalParties : []).forEach((p) => {
            const cid = p.client?.client_id || p.client_id
            if (cid) lookup[cid] = { primary_party_id: caseData.defendant.clientId, client_id: cid, role: p.role || 'defendant' }
        })
        return lookup
    }, [caseData])

    const is_cancled_case = caseData?.rawStatus === 'cancelled' || caseData?.status === 'Cancelled'
    const is_gsc_case = caseData?.is_gsc_case === true || (caseData?.party_emails?.length || 0) > 0
    const is_car_case = caseData?.is_car_case === true
    // A CAR case that's still in the CAR backend (UUID id, hasn't been
    // scheduled by CAR admin yet, so it never crossed into scale-DB).
    // Derived from the BE's case-detail UUID fallback (caseController.js
    // returns source:'car-pending' + status:'Awaiting CAR scheduling').
    const is_car_case_not_scheduled =
        caseData?.source === 'car-pending' ||
        caseData?.rawStatus === 'awaiting_scheduling' ||
        caseData?.rawStatus === 'awaiting_mediator_approval'
    const isSystemCoupon = caseData?.coupon?.system_coupon_id === 1
    const is_odr_mediator = caseData?.mediatorSettings?.is_odr_mediator === true || caseData?.mediator?.isOdrMediator === true

    // Mirrors Scale's getPartyLabelByRole(): ODR-typed mediators see neutral
    // "Party 1 / Party 2" labels, GSC cases collapse to a single FLA-team
    // label, and system-coupon / CAR cases use Initiating / Responding.
    const labelFor = (role) => {
        if (is_gsc_case) return role === 'plaintiff' ? 'FLA Lawyer Team' : 'Responding Party'
        if (isSystemCoupon || is_car_case_not_scheduled || is_car_case) {
            return role === 'plaintiff' ? 'Initiating Party' : 'Responding Party'
        }
        if (is_odr_mediator) return role === 'plaintiff' ? 'Party 1' : 'Party 2'
        return role === 'plaintiff' ? 'Plaintiff' : 'Defendant'
    }
    const plaintiffPartyLabel = labelFor('plaintiff')
    const defendantPartyLabel = labelFor('defendant')

    return (
        <CaseDetailContext.Provider
            value={{
                caseData,
                setCaseData,
                refreshCaseData,
                transferHistory,
                setTransferHistory,
                partyLookup,
                is_cancled_case,
                is_gsc_case,
                is_car_case_not_scheduled,
                is_car_case,
                isSystemCoupon,
                is_odr_mediator,
                plaintiffPartyLabel,
                defendantPartyLabel,
            }}
        >
            {children}
        </CaseDetailContext.Provider>
    )
}
